import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Bed,
  Bath,
  Maximize,
  MapPin,
  Navigation,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Car,
  Clock,
  Route as RouteIcon,
  Phone,
  Mail,
  Compass,
  Send,
} from "lucide-react";
import { properties } from "../../data/malickeCase";
import { PropertyMap } from "./PropertyMap";

const WEB3_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

export function PropertyDetailModal({ propertyId, onClose }) {
  const property = properties.find((p) => p.id === propertyId);
  const [inquiryName, setInquiryName] = useState("");
  const [inquiryEmail, setInquiryEmail] = useState("");
  const [inquiryPhone, setInquiryPhone] = useState("");
  const [inquiryNote, setInquiryNote] = useState("");
  const [inquiryStatus, setInquiryStatus] = useState("idle"); // idle | sending | done | error

  async function handleInquiry(e) {
    e.preventDefault();
    setInquiryStatus("sending");
    try {
      const fd = new FormData();
      fd.append("access_key", WEB3_KEY);
      fd.append("subject", `Property inquiry  -  ${property.name}`);
      fd.append("from_name", inquiryName);
      fd.append("email", inquiryEmail);
      fd.append("replyto", inquiryEmail);
      fd.append("phone", inquiryPhone);
      fd.append("property", property.name);
      fd.append("location", property.location);
      fd.append("message", inquiryNote || "Interested in viewing this property.");
      const res = await fetch("https://api.web3forms.com/submit", { method: "POST", body: fd });
      const data = await res.json();
      setInquiryStatus(data.success ? "done" : "error");
    } catch {
      setInquiryStatus("error");
    }
  }

  useEffect(() => {
    if (propertyId) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [propertyId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <AnimatePresence>
      {property && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-brand-ink/70 backdrop-blur-sm p-0 md:p-4 lg:p-6 overflow-y-auto"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="relative mx-auto my-0 md:my-4 max-w-6xl bg-white rounded-none md:rounded-3xl overflow-hidden shadow-lifted-mc"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 grid h-10 w-10 place-items-center rounded-full bg-white/90 backdrop-blur shadow-soft-mc hover:bg-white hover:scale-105 transition-all"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-ink-mc" />
            </button>

            <div className="grid lg:grid-cols-2">
              <div className="lg:max-h-[92vh] lg:overflow-y-auto scroll-soft">
                <Gallery
                  key={property.id}
                  images={property.gallery}
                  status={property.status}
                  student={property.student}
                />

                <div className="p-6 md:p-8 space-y-6">
                  <header>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-coral">Sample listing</p>
                    <p className="text-xs font-semibold tracking-widest uppercase text-coral">
                      {property.type}  /  {property.parish}
                    </p>
                    <h2 className="font-display-mc text-3xl md:text-4xl font-bold text-ink-mc mt-1 leading-tight">
                      {property.name}
                    </h2>
                    <p className="text-ink-soft-mc mt-2">{property.tagline}</p>
                    <p className="text-2xl font-bold text-ink-mc mt-3">
                      {property.price}
                      {property.pricePeriod && (
                        <span className="text-sm text-ink-soft-mc font-normal">
                          {" "}
                          {property.pricePeriod}
                        </span>
                      )}
                    </p>
                  </header>

                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { icon: Bed, label: "Bedrooms", value: property.beds },
                      { icon: Bath, label: "Bathrooms", value: property.baths },
                      { icon: Maximize, label: "Sq ft", value: property.sqft.toLocaleString() },
                    ].map((s) => (
                      <div key={s.label} className="rounded-2xl bg-sand p-4 text-center">
                        <s.icon className="h-5 w-5 text-coral mx-auto" />
                        <p className="font-display-mc text-xl font-bold text-ink-mc mt-1.5">
                          {s.value}
                        </p>
                        <p className="text-xs text-ink-soft-mc">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  <div>
                    <h3 className="font-display-mc text-lg font-bold text-ink-mc">
                      About this property
                    </h3>
                    <p className="mt-2 text-ink-soft-mc leading-relaxed">
                      {property.description}
                    </p>
                  </div>

                  <div>
                    <h3 className="font-display-mc text-lg font-bold text-ink-mc">
                      {property.crewRenovated ? "What we renovated" : "Property features"}
                    </h3>
                    <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                      {property.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-ink-mc">
                          <CheckCircle2 className="h-4 w-4 text-coral flex-shrink-0 mt-0.5" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-display-mc text-lg font-bold text-ink-mc">
                      Amenities
                    </h3>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {property.amenities.map((a) => (
                        <span
                          key={a}
                          className="rounded-full bg-sand border border-brand/10 px-3 py-1.5 text-xs font-medium text-ink-mc"
                        >
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-sand lg:max-h-[92vh] lg:overflow-y-auto scroll-soft">
                <div className="p-6 md:p-8 space-y-5">
                  <header>
                    <p className="text-xs font-semibold tracking-widest uppercase text-coral">
                      Location
                    </p>
                    <h3 className="font-display-mc text-2xl font-bold text-ink-mc mt-1 flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-coral" />
                      {property.location}
                    </h3>
                    <p className="text-xs text-ink-soft-mc mt-1.5">
                      Lat {property.coordinates.lat.toFixed(4)}°, Lng{" "}
                      {property.coordinates.lng.toFixed(4)}°
                    </p>
                  </header>

                  <PropertyMap property={property} height="360px" />

                  <div className="rounded-2xl bg-white border border-brand/10 p-4 flex items-center gap-4">
                    <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand text-coral">
                      <Car className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-ink-mc">
                        From: {property.drivingFrom.label}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-ink-soft-mc">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5 text-coral" />
                          {property.estDriveTime} drive
                        </span>
                        <span className="flex items-center gap-1">
                          <RouteIcon className="h-3.5 w-3.5 text-coral" />
                          {property.estDistance}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&origin=${property.drivingFrom.coordinates.lat},${property.drivingFrom.coordinates.lng}&destination=${property.coordinates.lat},${property.coordinates.lng}&travelmode=driving`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-coral px-4 py-3 text-sm font-semibold text-white shadow-coral-glow hover:scale-[1.02] transition-transform"
                    >
                      <Navigation className="h-4 w-4" />
                      Get directions
                    </a>
                    <a
                      href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${property.coordinates.lat},${property.coordinates.lng}&heading=235&pitch=10`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-4 py-3 text-sm font-semibold hover:bg-brand-ink transition-colors"
                    >
                      <Compass className="h-4 w-4" />
                      Street view
                    </a>
                  </div>

                  <div className="rounded-2xl bg-white border border-brand/10 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <RouteIcon className="h-5 w-5 text-coral" />
                      <h4 className="font-display-mc text-lg font-bold text-ink-mc">
                        Drive there: step by step
                      </h4>
                    </div>
                    <ol className="relative border-l-2 border-coral/30 ml-3 space-y-4">
                      {property.directions.map((d, i) => (
                        <li key={i} className="pl-5 relative">
                          <span className="absolute -left-[11px] top-1 grid h-5 w-5 place-items-center rounded-full bg-coral text-white text-[10px] font-bold ring-2 ring-white">
                            {i + 1}
                          </span>
                          <p className="text-sm text-ink-mc leading-relaxed">
                            {d.step}
                          </p>
                          <p className="text-xs text-ink-soft-mc mt-1 flex items-center gap-1">
                            <RouteIcon className="h-3 w-3 text-coral" />
                            {d.distance}
                          </p>
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div>
                    <p className="text-xs font-semibold tracking-widest uppercase text-coral mb-2">
                      See the approach
                    </p>
                    <div className="rounded-2xl overflow-hidden border border-brand/10 aspect-[16/9]">
                      <iframe
                        title={`Street view near ${property.name}`}
                        src={`https://www.google.com/maps?q=${property.coordinates.lat},${property.coordinates.lng}&z=16&output=embed`}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        allowFullScreen
                      />
                    </div>
                    <p className="text-xs text-ink-soft-mc mt-2 leading-relaxed">
                      Tap "Street view" above to drop into Google Maps and virtually drive the last stretch to the property. Follow the arrows and signs just like a real visit.
                    </p>
                  </div>

                  <div className="rounded-2xl bg-brand text-white p-5">
                    <h4 className="font-display-mc text-lg font-bold">
                      Interested in this property?
                    </h4>
                    <p className="text-sm text-white/70 mt-1 leading-relaxed">
                      Call us, send an email, or fill in the form and we'll get back to you same day.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <a href="tel:+18768279793" className="inline-flex items-center gap-1.5 rounded-full bg-coral px-4 py-2 text-sm font-semibold text-white">
                        <Phone className="h-3.5 w-3.5" />
                        +1 (876) 827-9793
                      </a>
                      <a href="mailto:malickecase28@gmail.com" className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/20">
                        <Mail className="h-3.5 w-3.5" />
                        Email us
                      </a>
                    </div>

                    {inquiryStatus === "done" ? (
                      <div className="mt-4 rounded-xl bg-white/10 p-4 text-center">
                        <CheckCircle2 className="h-6 w-6 text-coral mx-auto mb-1" />
                        <p className="text-sm font-semibold">Inquiry sent.</p>
                        <p className="text-xs text-white/60 mt-0.5">We'll reach out within a few hours to arrange a viewing.</p>
                      </div>
                    ) : (
                      <form onSubmit={handleInquiry} className="mt-4 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <input required value={inquiryName} onChange={e => setInquiryName(e.target.value)} placeholder="Your name"
                            className="rounded-xl bg-white/10 border border-white/20 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-coral" />
                          <input required type="email" value={inquiryEmail} onChange={e => setInquiryEmail(e.target.value)} placeholder="Email address"
                            className="rounded-xl bg-white/10 border border-white/20 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-coral" />
                        </div>
                        <input type="tel" value={inquiryPhone} onChange={e => setInquiryPhone(e.target.value)} placeholder="Phone (optional)"
                          className="w-full rounded-xl bg-white/10 border border-white/20 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-coral" />
                        <textarea rows={2} value={inquiryNote} onChange={e => setInquiryNote(e.target.value)} placeholder="Any questions or notes"
                          className="w-full rounded-xl bg-white/10 border border-white/20 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-coral resize-none" />
                        {inquiryStatus === "error" && <p className="text-xs text-red-300">Something went wrong. Call us directly.</p>}
                        <button type="submit" disabled={inquiryStatus === "sending"}
                          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white text-brand px-4 py-2.5 text-sm font-semibold hover:bg-white/90 transition-colors disabled:opacity-60">
                          <Send className="h-3.5 w-3.5" />
                          {inquiryStatus === "sending" ? "Sending..." : "Send inquiry"}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Gallery({ images, status, student }) {
  const [idx, setIdx] = useState(0);
  return (
    <>
      <div className="relative aspect-[4/3] bg-sand">
        <img
          src={images[idx]}
          alt={`Property view ${idx + 1}`}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <button
          onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
          className="absolute left-3 top-1/2 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full bg-white/90 backdrop-blur shadow-soft-mc hover:bg-white"
          aria-label="Previous image"
        >
          <ChevronLeft className="h-5 w-5 text-ink-mc" />
        </button>
        <button
          onClick={() => setIdx((i) => (i + 1) % images.length)}
          className="absolute right-3 top-1/2 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full bg-white/90 backdrop-blur shadow-soft-mc hover:bg-white"
          aria-label="Next image"
        >
          <ChevronRight className="h-5 w-5 text-ink-mc" />
        </button>
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "w-6 bg-coral" : "w-1.5 bg-white/70 hover:bg-white"
              }`}
              aria-label={`Go to image ${i + 1}`}
            />
          ))}
        </div>
        <div className="absolute top-4 left-4 flex flex-col gap-1.5 items-start">
          <span className="rounded-full bg-coral px-3 py-1 text-xs font-semibold text-white shadow-soft-mc">
            {status}
          </span>
          {student && (
            <span className="rounded-full bg-white/95 backdrop-blur px-3 py-1 text-xs font-semibold text-coral shadow-soft-mc">
              Student
            </span>
          )}
        </div>
      </div>

      <div className="flex gap-2 p-4 overflow-x-auto no-scrollbar bg-white">
        {images.map((g, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            className={`flex-shrink-0 h-16 w-20 rounded-lg overflow-hidden border-2 transition-all ${
              i === idx
                ? "border-coral"
                : "border-transparent opacity-70 hover:opacity-100"
            }`}
          >
            <img src={g} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </>
  );
}
